import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

const VIDEOS_PER_PAGE = 9;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState(slug || '91-video');

  const category = categories.find((c) => c.slug === activeCategory);

  const filteredVideos = useMemo(() => {
    return videos.filter((v) => v.category === activeCategory);
  }, [activeCategory]);

  const totalPages = Math.ceil(filteredVideos.length / VIDEOS_PER_PAGE);
  const paginatedVideos = filteredVideos.slice(
    (currentPage - 1) * VIDEOS_PER_PAGE,
    currentPage * VIDEOS_PER_PAGE,
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="pt-20 text-center py-20">
          <p className="text-foreground-500">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`${category.name},桃子视频,数字系列视频,在线观看,高清电影,电视剧`}
        canonicalUrl={`https://taozivideo.com/category/${activeCategory}`}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
          </div>

          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-7xl mx-auto text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500 mx-auto mb-4">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {category.name}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                {category.description}
              </p>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto py-3 overflow-x-auto">
            <div className="flex items-center gap-2 min-w-max">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            {paginatedVideos.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6" data-product-shop="">
                  {paginatedVideos.map((video) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative w-full aspect-video overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-sm whitespace-nowrap">
                            {video.quality}
                          </span>
                          <span className="px-2.5 py-1 rounded-full bg-primary-500/80 text-white text-xs font-medium backdrop-blur-sm whitespace-nowrap">
                            {video.rating}分
                          </span>
                        </div>
                        <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-medium backdrop-blur-sm whitespace-nowrap">
                          {video.duration}
                        </div>
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <i className="ri-play-fill text-2xl"></i>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 md:p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-foreground-400 whitespace-nowrap">{video.categoryName}</span>
                          <span className="text-xs text-foreground-300">·</span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">{video.year}</span>
                        </div>
                        <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-1">
                          {video.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                          {video.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-foreground-400">
                          <span className="flex items-center gap-1 whitespace-nowrap">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-eye-line text-xs"></i>
                            </div>
                            {video.views}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-left-s-line text-sm"></i>
                      </div>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-all cursor-pointer ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50'
                            : 'border border-background-200/70 text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-s-line text-sm"></i>
                      </div>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-300 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频资源</p>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}